//ICS-203
import React from 'react';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { useSearchParams } from 'react-router';

import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import { FormExportButton } from 'components/IAP/FormExportButton';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import FormVersioningBanner from 'components/IAP/femaCards/FormVersioningBanner';
import {
  useCurrentPeriod,
  useIncident,
  useIncidentPermissions,
} from 'components/IAP/hooks/incidentHooks';
import { useRecordFormVersionSave } from 'components/IAP/hooks/icsFormVersionHooks';
import { useIncidentInfo } from 'components/IAP/hooks/incidentInfoHooks';
import { OperationalPeriodScheduleControl } from './OperationalPeriodScheduleControl';
import { useICSContext } from 'components/IAP/planningView/ICSContext';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { useGetRosterForIncident } from 'hooks/incident/useRoster';

import {
  useCreateIncidentAgency,
  useDeleteIncidentAgency,
  useIncidentAgencies,
  useUpdateIncidentAgency,
} from '../../hooks/incidentAgenciesHooks';
import {
  useCreateIncidentBranch,
  useDeleteIncidentBranch,
  useIncidentBranches,
  useUpdateIncidentBranch,
} from '../../hooks/incidentBranchHooks';
import {
  useCreateIncidentDivision,
  useDeleteIncidentDivision,
  useIncidentDivisions,
  useUpdateIncidentDivision,
} from '../../hooks/incidentDivisionHooks';
import {
  useIcsIncidentOrganizationAssignments,
  useUpsertIcsIncidentOrganizationAssignment,
} from '../../hooks/icsIncidentOrganizationAssignmentsHooks';
import { getIncidentCardTitle } from '../USCGPage';
import { Ics204CardSection } from './AssignmentList/ics204BoxLabels';
import { BoxInfo, PrintedValue } from './icsBoxPrimitives';
import Ics203SignatureBox from './Ics203SignatureBox';
import Ics203AgencyTable from './Ics203Table/Ics203AgencyTable';
import Ics203BranchList from './Ics203Table/Ics203BranchList';
import Ics203CommanderTable from './Ics203Table/Ics203CommanderTable';
import Ics203DivisionList from './Ics203Table/Ics203DivisionList';
import Ics203PositionField from './Ics203Table/Ics203PositionField';
import {
  ICS203_AGENCY_HINT,
  ICS203_BOX_LABELS,
  ICS203_COMMAND_STAFF,
  ICS203_COMMAND_STAFF_HINT,
  ICS203_FINANCE_SECTION,
  ICS203_HEADER_DOC_TOOLTIP,
  ICS203_INCIDENT_LOCATION_HINT,
  ICS203_INCIDENT_NAME_HINT,
  ICS203_INTELLIGENCE_SECTION,
  ICS203_LOGISTICS_SECTION,
  ICS203_OPERATIONAL_PERIOD_HINT,
  ICS203_OPERATIONS_HINT,
  ICS203_OPERATIONS_SECTION,
  ICS203_PLANNING_SECTION,
} from './ics203BoxLabels';

const ICS_203_EXPORT_FORMNAME = 'ICS_203_CG';

/**
 * Organization Assignment List, the ICS 203-CG.
 *
 * Boxes 1-3 are the read-only header. Boxes 4-7 are the printed sheet's left
 * column and 8-10 its right, which the grid below reproduces on a wide screen
 * and stacks on a narrow one. Box 11 is the Resources Unit Leader's signature.
 *
 * The card is documentation only: seats filled by the Incident Roster print
 * read-only, because writing back to the roster from here is not something the
 * form does, which is what the title's tooltip says.
 */
const OrganizationAssignmentList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');

  const { periodNumber: currentPeriodNumber } = useCurrentPeriod();
  // Future tab is a planning period — no operational_period row exists yet,
  // so omit periodNumber and let the backend fall back to
  // ics_incident_info.next_operational_period_{start,anticipated_end}_date_time.
  const exportPeriodNumber =
    mode === 'future' ? undefined : currentPeriodNumber;

  const incidentId = useActualIncidentId();

  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;
  const { effectivePeriodNumber, isFuture, readOnlyForms } = useICSContext();
  const { isMapVisible } = useUscgMapContext();
  const readOnly = !canEditCards || readOnlyForms;

  const incident = useIncident();
  const { data: incidentInfo } = useIncidentInfo();

  const { data: rosterData } = useGetRosterForIncident(incidentId);
  const { data: agenciesData } = useIncidentAgencies(incidentId);
  const { mutate: createAgency } = useCreateIncidentAgency();
  const { mutate: updateAgency } = useUpdateIncidentAgency();
  const { mutate: deleteAgency } = useDeleteIncidentAgency();

  const { data: divisions } = useIncidentDivisions(incidentId);
  const { mutate: createIncidentDivision } = useCreateIncidentDivision();
  const { mutate: updateIncidentDivision } = useUpdateIncidentDivision();
  const { mutate: deleteIncidentDivision } = useDeleteIncidentDivision();

  const { data: branches } = useIncidentBranches(incidentId);
  const { mutate: createIncidentBranch } = useCreateIncidentBranch();
  const { mutate: updateIncidentBranch } = useUpdateIncidentBranch();
  const { mutate: deleteIncidentBranch } = useDeleteIncidentBranch();

  const { data: positionAssignments } =
    useIcsIncidentOrganizationAssignments(incidentId);
  const { mutate: upsertPositionAssignment } =
    useUpsertIcsIncidentOrganizationAssignment();

  /**
   * The 203 is a versioned form, so each committed box should leave a draft
   * version behind. Every write here is an explicit Save rather than a
   * debounced keystroke, which is what keeps one save to one version.
   */
  const recordVersionSave = useRecordFormVersionSave(
    'ICS_203',
    effectivePeriodNumber
  );
  const recordVersion = React.useCallback(() => {
    if (!readOnlyForms) recordVersionSave.mutate();
  }, [readOnlyForms, recordVersionSave]);

  const assignments = React.useMemo(() => {
    const map = new Map<string, string>();
    (positionAssignments ?? []).forEach((assignment) => {
      if (assignment.position_key) {
        map.set(assignment.position_key, assignment.name ?? '');
      }
    });
    return map;
  }, [positionAssignments]);

  const savePosition = React.useCallback(
    (positionKey: string, value: string) => {
      if (!incidentId) return;
      upsertPositionAssignment(
        { incident_id: incidentId, position_key: positionKey, name: value },
        { onSuccess: recordVersion }
      );
    },
    [incidentId, recordVersion, upsertPositionAssignment]
  );

  const saveCommanderRow = React.useCallback(
    (patch: { deputy: string; agency: string }, seat: string) => {
      if (!incidentId) return;
      upsertPositionAssignment({
        incident_id: incidentId,
        position_key: `${seat} Deputy`,
        name: patch.deputy,
      });
      upsertPositionAssignment(
        {
          incident_id: incidentId,
          position_key: `${seat} Agency`,
          name: patch.agency,
        },
        { onSuccess: recordVersion }
      );
    },
    [incidentId, recordVersion, upsertPositionAssignment]
  );

  /** Shared by boxes 8 and 9, whose rows differ only in the section they sit in. */
  const renameDivision = React.useCallback(
    (division: { id?: string }, name: string) => {
      const record = (divisions ?? []).find(
        (candidate) => candidate.id === division.id
      );
      if (!record) return;
      updateIncidentDivision({ ...record, name }, { onSuccess: recordVersion });
    },
    [divisions, recordVersion, updateIncidentDivision]
  );

  const intelligenceDivisions = React.useMemo(
    () =>
      (divisions ?? []).filter(
        (division) => division?.section === 'intelligence'
      ),
    [divisions]
  );

  const { exportAsForm, isExporting } = useIcsFormPdfPreview();
  const handleExport = (format: 'pdf' | 'docx' = 'pdf') => {
    void exportAsForm({
      formname: ICS_203_EXPORT_FORMNAME,
      data: {},
      mapSketch: null,
      periodNumber: exportPeriodNumber,
      format,
    });
  };

  const handleBranchDelete = (branch: { id?: string }) => {
    // A division's `section` is its branch id, so the divisions go with it
    // rather than being orphaned into a section that no longer prints.
    (divisions ?? [])
      .filter((division) => division.section === branch.id)
      .forEach((division) => {
        if (division.id) deleteIncidentDivision(division.id);
      });
    if (branch.id)
      deleteIncidentBranch(branch.id, { onSuccess: recordVersion });
  };

  const positionFieldProps = {
    rosterData,
    assignments,
    onSave: savePosition,
    readOnly,
  };

  const exportActions = (
    <FormExportButton
      formname={ICS_203_EXPORT_FORMNAME}
      onExport={handleExport}
      label="Preview ICS-203"
      disabled={isExporting}
    />
  );

  /** Boxes 6 to 10, each a column of named blanks over optional rows. */
  const simpleSection = (
    boxLabel: string,
    testId: string,
    positions: readonly { label: string; positionKey: string }[],
    extra?: React.ReactNode,
    hint?: string
  ) => (
    <Ics204CardSection
      boxLabel={boxLabel}
      testId={testId}
      className="tw:mt-0 tw:h-full tw:min-w-0"
      headerAction={
        hint ? <BoxInfo label={boxLabel}>{hint}</BoxInfo> : undefined
      }
    >
      <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
        {positions.map((position) => (
          <Ics203PositionField
            key={position.positionKey}
            {...positionFieldProps}
            label={position.label}
            positionKey={position.positionKey}
          />
        ))}
        {extra}
      </div>
    </Ics204CardSection>
  );

  const body = (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
      <FormVersioningBanner baseId="ICS_203" />

      <div className="tw:grid tw:gap-3 tw:md:grid-cols-3">
        <Ics204CardSection
          boxLabel={ICS203_BOX_LABELS.incidentName}
          testId="ics203-box-incident-name"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label={ICS203_BOX_LABELS.incidentName}>
              {ICS203_INCIDENT_NAME_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={incident.data?.name ?? ''}
            testId="ics203-incident-name"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS203_BOX_LABELS.incidentLocation}
          testId="ics203-box-incident-location"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label={ICS203_BOX_LABELS.incidentLocation}>
              {ICS203_INCIDENT_LOCATION_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={
              incidentInfo?.incidentLocation || incident.data?.address || ''
            }
            testId="ics203-incident-location"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS203_BOX_LABELS.operationalPeriod}
          testId="ics203-box-operational-period"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label={ICS203_BOX_LABELS.operationalPeriod}>
              {ICS203_OPERATIONAL_PERIOD_HINT}
            </BoxInfo>
          }
        >
          <OperationalPeriodScheduleControl testId="ics203-operational-period" />
        </Ics204CardSection>
      </div>

      {/* The printed sheet runs boxes 4-7 down the left and 8-10 down the
          right. Below `lg` they stack in numbered order instead. */}
      <div className="tw:grid tw:min-w-0 tw:gap-3 tw:lg:grid-cols-2">
        <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3 tw:[&>[data-slot=card]]:shrink-0">
          <Ics204CardSection
            boxLabel={ICS203_BOX_LABELS.commandStaff}
            testId="ics203-box-command-staff"
            className="tw:mt-0 tw:min-w-0 tw:shrink-0"
            headerAction={
              <BoxInfo label={ICS203_BOX_LABELS.commandStaff}>
                {ICS203_COMMAND_STAFF_HINT}
              </BoxInfo>
            }
          >
            <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
              <Ics203CommanderTable
                rosterData={rosterData}
                assignments={assignments}
                onSave={saveCommanderRow}
                readOnly={readOnly}
              />
              <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
                {ICS203_COMMAND_STAFF.map((position) => (
                  <Ics203PositionField
                    key={position.positionKey}
                    {...positionFieldProps}
                    label={position.label}
                    positionKey={position.positionKey}
                  />
                ))}
              </div>
            </div>
          </Ics204CardSection>

          <Ics204CardSection
            boxLabel={ICS203_BOX_LABELS.agencyRepresentatives}
            testId="ics203-box-agencies"
            className="tw:mt-0 tw:min-w-0"
            headerAction={
              <BoxInfo label={ICS203_BOX_LABELS.agencyRepresentatives}>
                {ICS203_AGENCY_HINT}
              </BoxInfo>
            }
          >
            <Ics203AgencyTable
              agencies={agenciesData ?? []}
              readOnly={readOnly}
              onCreate={() => {
                if (!incidentId) return;
                createAgency(
                  {
                    incident_id: incidentId,
                    agency_name: '',
                    representative_name: '',
                  },
                  { onSuccess: recordVersion }
                );
              }}
              onUpdate={(agency, patch) => {
                // Spread the server's record rather than the table's view of
                // it, so a column the table never renders is not dropped.
                const record = (agenciesData ?? []).find(
                  (candidate) => candidate.id === agency.id
                );
                if (!record) return;
                updateAgency(
                  { ...record, ...patch },
                  { onSuccess: recordVersion }
                );
              }}
              onDelete={(id) => deleteAgency(id, { onSuccess: recordVersion })}
            />
          </Ics204CardSection>

          {simpleSection(
            ICS203_BOX_LABELS.planningSection,
            'ics203-box-planning',
            ICS203_PLANNING_SECTION
          )}

          {simpleSection(
            ICS203_BOX_LABELS.logisticsSection,
            'ics203-box-logistics',
            ICS203_LOGISTICS_SECTION
          )}
        </div>

        <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3 tw:[&>[data-slot=card]]:shrink-0">
          {simpleSection(
            ICS203_BOX_LABELS.operationsSection,
            'ics203-box-operations',
            ICS203_OPERATIONS_SECTION,
            <Ics203BranchList
              branches={branches ?? []}
              divisions={divisions ?? []}
              readOnly={readOnly}
              onCreateBranch={() => {
                if (!incidentId) return;
                createIncidentBranch(
                  {
                    incident_id: incidentId,
                    name: '',
                    branch_director_name: '',
                    branch_deputy: '',
                    division_list: [],
                  },
                  { onSuccess: recordVersion }
                );
              }}
              onUpdateBranch={(branch, patch) => {
                const record = (branches ?? []).find(
                  (candidate) => candidate.id === branch.id
                );
                if (!record) return;
                updateIncidentBranch(
                  { ...record, ...patch },
                  { onSuccess: recordVersion }
                );
              }}
              onDeleteBranch={handleBranchDelete}
              onCreateDivision={(branchId) => {
                if (!incidentId) return;
                createIncidentDivision(
                  {
                    incident_id: incidentId,
                    name: '',
                    division_supervisor_name: '',
                    section: branchId,
                  },
                  { onSuccess: recordVersion }
                );
              }}
              onRenameDivision={renameDivision}
              onDeleteDivision={(id) =>
                deleteIncidentDivision(id, { onSuccess: recordVersion })
              }
            />,
            ICS203_OPERATIONS_HINT
          )}

          {simpleSection(
            ICS203_BOX_LABELS.intelligenceSection,
            'ics203-box-intelligence',
            ICS203_INTELLIGENCE_SECTION,
            <Ics203DivisionList
              testId="ics203-intelligence-divisions"
              divisions={intelligenceDivisions}
              readOnly={readOnly}
              onCreate={() => {
                if (!incidentId) return;
                createIncidentDivision(
                  {
                    incident_id: incidentId,
                    name: '',
                    division_supervisor_name: '',
                    section: 'intelligence',
                  },
                  { onSuccess: recordVersion }
                );
              }}
              onRename={renameDivision}
              onDelete={(id) =>
                deleteIncidentDivision(id, { onSuccess: recordVersion })
              }
            />
          )}

          {simpleSection(
            ICS203_BOX_LABELS.financeSection,
            'ics203-box-finance',
            ICS203_FINANCE_SECTION
          )}
        </div>
      </div>

      <Ics203SignatureBox periodNumber={effectivePeriodNumber} />
    </div>
  );

  // Beside the map on a future period there is no room for card chrome, so the
  // boxes stand on the panel itself, as the 202 does.
  if (isMapVisible && isFuture) {
    return <div className="dark content tw:text-foreground mt-2">{body}</div>;
  }

  return (
    <Card
      data-testid="uscg-203-card"
      data-tour="ics203-card"
      data-form-family="uscg"
      className="dark tw:bg-background tw:text-foreground"
    >
      <CardHeader>
        <CardTitle className="tw:flex tw:items-center tw:gap-2">
          {getIncidentCardTitle('organizationAssignmentList')}
          <BoxInfo label={ICS203_HEADER_DOC_TOOLTIP}>
            {ICS203_HEADER_DOC_TOOLTIP}
          </BoxInfo>
        </CardTitle>
        <CardAction>{exportActions}</CardAction>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
};

export default OrganizationAssignmentList;
